import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { LayoutService, ScreenSize } from 'libs/portal/src/layout/layout.service';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { MemoryService } from 'libs/portal/src/services/memory/memory.service';
import { PaginationService } from 'libs/portal/src/services/pagination/pagination.service';
import { ToastrService } from 'ngx-toastr';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { Endpoints } from '@huge-monorepo/portal/config';
import { CardTableComponent } from '../../tools/card-table/components';
import { CardTableOptions } from '../../tools/card-table/models';
import { CardTableHeader } from '../../tools/card-table/models/card-table-header.model';
import { GenericTableHeaderOptions, RefreshData } from '../../tools/card-table/models/generic-table-header-options.model';
import { openExportDialog } from '../../tools/export-modal/export-modal.component';

@Component({
  selector: 'app-keep-alive-activity-report',
  templateUrl: './keep-alive-activity-report.component.html',
  styleUrls: ['./keep-alive-activity-report.component.scss'],
})
export class KeepAliveActivityReportComponent implements OnInit {
  @ViewChild('KATable', { static: false }) public KATable: CardTableComponent;

  headerOptions: GenericTableHeaderOptions = {
    ShowSearch: true,
    SearchPlaceholder: 'Search Keep Alive Devices',
    DateItem: {
      Singular: false,
      Title: 'Select Report Range',
      StartDate: this.tools.StartOfMonth().toDateString(),
      EndDate: new Date().toDateString(),
    },
    DateRequired: true,
    EnableExport: true,
    EnableSelectAll: true,
    SortItems: [
      {
        Name: 'TerminalID',
        Display: 'Terminal ID',
      },
      {
        Name: 'CDRDeviceID',
        Display: 'CDR Device',
      },
      {
        Name: 'MerchantName',
        Display: 'Merchant',
      },
      {
        Name: 'TerminalType',
        Display: 'Type',
      },
      {
        Name: 'RingfenceRadius',
        Display: 'Ringfence',
      },
    ],
  };

  ScreenSize = ScreenSize;

  keepAliveData: any[] = [];
  totalKeepAliveDevices: number;

  loadingKADevices = false;
  allSelected = false;
  reportRange: {
    start: string;
    end: string;
  } = { start: null, end: null };

  kaOrderBy = 'TerminalID';
  kaTableCount = 10;
  kaSearch = '';
  kaTableOffset = 0;
  kaCurrentPage = 1;
  kaDesc = false;
  search = false;

  firstLoad = true;
  firstKALoad = true;
  obfuscateData = false;
  authSubscription: Subscription;

  getKeepAliveDevicesRequestBody = {
    Online: null,
    Terminals: [],
    Merchants: [],
    GroupID: null,
  };

  constructor(
    public layoutService: LayoutService,
    private genericService: GenericService,
    private paginationService: PaginationService,
    private endpoints: Endpoints,
    private tools: ToolsService,
    private auth: UserRightsService,
    private memory: MemoryService,
    private toastService: ToastrService,
    private dialogRef: MatDialog,
  ) {}

  keepAliveTableOptions: CardTableOptions = {
    titleProperty: 'terminalid',
    subTitleProperty: 'terminalType',
    additionalProperty: 'acquiringBank',
    rowId: 'id',
    contentProperty: 'keepAliveServerName',
    indicatorColorProperty: 'color',
    selectedProperty: 'selected',
    isSelectable: true,
    flexCardLayout: false,
    propertyRowExclusions: ['color', 'selected', 'id', 'cdrdevice_id'],
    stylePropertyPrefix: 'keepAliveDevices',
    clickableFlexCard: false,
    textSize: 'regular',
  };

  keepAliveTableHeaders: CardTableHeader[] = [
    {
      ID: 'terminalid',
      Text: 'Terminal ID',
      Width: 12,
    },
    {
      ID: 'deviceNum',
      Text: 'CDR Device',
      Width: 12,
    },
    {
      ID: 'cdrOnline',
      Text: 'CDR',
      Width: 6,
      Type: 'tooltipCircle',
      TooltipText: 'CDR Device Status',
      TooltipPosition: 'right',
    },
    {
      ID: 'kaOnline',
      Text: 'KA',
      Width: 6,
      Type: 'tooltipCircle',
      TooltipText: 'Keep Alive Device Status',
      TooltipPosition: 'right',
    },
    {
      ID: 'merchant',
      Text: 'Merchant',
      Width: 20,
    },
    {
      ID: 'terminalType',
      Text: 'Type',
      Width: 8,
    },
    {
      ID: 'ringfence',
      Text: 'Ringfence',
      Width: 10,
    },
    {
      ID: 'currentLocation',
      Text: 'Current Location',
      Width: 8,
    },
    {
      ID: 'distanceFromRingfence',
      Text: 'Distance From Ringfence',
      Width: 8,
    },
  ];

  setKATableToRefreshing() {
    if (this.KATable) {
      this.KATable.StartRefresh();
    }
  }

  setKATableToFinished() {
    if (this.KATable) {
      this.KATable.EndRefresh();
    }
  }

  refresh(data: RefreshData): void {
    this.kaSearch = data.Search ?? '';
    if (this.kaSearch) {
      this.search = true;
    } else {
      this.search = false;
    }
    this.kaOrderBy = data.Sort ?? '';
    this.kaDesc = data.Desc ?? this.kaDesc;
    this.kaTableOffset = 0;
    this.kaCurrentPage = 1;
    this.reportRange = {
      start: data.FromDate,
      end: data.ToDate,
    };
    this.getKeepAliveDevices(this.kaOrderBy, this.kaSearch, this.kaTableCount, this.kaTableOffset, this.kaDesc);
  }

  exportReport(data: RefreshData): void {
    this.reportRange.start = data.FromDate;
    this.reportRange.end = data.ToDate;
    if (this.allSelected || this.keepAliveData.some((elem) => elem.selected)) {
      
      let req = {
        StartDate: this.reportRange.start,
        EndDate: this.reportRange.end,
        AllKeepAliveDevices: false,
        KeepAliveDeviceIDs: this.keepAliveData
              .filter((elem) => elem.selected)
              .map((elem) => {
                return elem.id;
              }),
      };
      
      if(this.search == false){
        req = {
          StartDate: this.reportRange.start,
          EndDate: this.reportRange.end,
          AllKeepAliveDevices: this.allSelected,
          KeepAliveDeviceIDs: this.allSelected
            ? null
            : this.keepAliveData
                .filter((elem) => elem.selected)
                .map((elem) => {
                  return elem.id;
                }),
        };
      }

      openExportDialog(this.dialogRef, {
        endpoint: this.endpoints.DOWNLOAD_KEEP_ALIVE_ACTIVITY_REPORT,
        reqObj: req,
      });
    } else {
      this.toastService.info('', 'No devices selected', { timeOut: 1000 });
    }
  }

  getKeepAliveDevices(orderBy: string, search: string, count: number, offset: number, desc: boolean) {
    this.loadingKADevices = true;
    this.setKATableToRefreshing();
    console.log(search);
    console.log(orderBy);
    console.log(desc);
    this.genericService.POST(this.endpoints.GET_KEEP_ALIVE_DEVICES(search, count, offset, orderBy, desc), this.getKeepAliveDevicesRequestBody).subscribe((resp: Object) => {
      const deviceData = resp['body'];
      const tmpData = [];
      if (deviceData != null && deviceData.length > 0) {
        deviceData.forEach((element) => {
          const ringfenceRadius = element.RingfenceRadius != null ? element.RingfenceRadius / 1000 + ' km' : '';
          const acceptedLocation =
            element.AcceptedLocation != null &&
            element.AcceptedLocation.lat != null &&
            element.AcceptedLocation.lng != null &&
            element.AcceptedLocation.lng != 0 &&
            element.AcceptedLocation.lat != 0
              ? '(Known)'
              : '(Unknown)';
          const currentLocation =
            element.CurrentLocation != null &&
            element.CurrentLocation.lat != null &&
            element.CurrentLocation.lng != null &&
            element.CurrentLocation.lng != 0 &&
            element.CurrentLocation.lat != 0
              ? '(Known)'
              : '(Unknown)';
          console.log(element.DistanceFromRingFence);
          const distanceFromRingfence = ringfenceRadius != '-' && acceptedLocation != '(Unknown)' && currentLocation != '(Unknown)' ? element.DistanceFromRingfence : '-';
          const obj = {
            id: element.DeviceID,
            terminalid: element.TerminalID,
            cdrdevice_id: element.CDRDeviceID,
            deviceNum: element.DeviceNum ? element.DeviceNum : '',
            cdrOnline: { status: element.CDROnline, lastSeen: element.CDRLastSeen },
            kaOnline: { status: element.KAOnline, lastSeen: element.KALastSeen },
            merchant: element.MerchantName ? (this.obfuscateData ? this.tools.Obfuscate(element.MerchantName, 'Merchant', 8) : element.MerchantName) : '',
            terminalType: element.TerminalType,
            ringfence: ringfenceRadius + ' ' + acceptedLocation,
            currentLocation: currentLocation,
            distanceFromRingfence: distanceFromRingfence != '-' ? this.formatDistance(distanceFromRingfence) : '-',
            selected: false,
            color: element.Online ? '#3FC944' : '#C96D01',
          };
          tmpData.push(obj);
        });
      }
      this.keepAliveData = tmpData;
      this.totalKeepAliveDevices = this.tools.ExtractPaginationFromHeaders(resp['headers']);

      setTimeout(() => {
        this.loadingKADevices = false;
        this.setKATableToFinished();
        this.firstKALoad = false;
        this.paginationService.emit();
      });
    });
  }

  getPagedDevices(pageIndex: number) {
    this.kaTableOffset = (pageIndex - 1) * this.kaTableCount;
    this.kaCurrentPage = pageIndex;
    this.getKeepAliveDevices(this.kaOrderBy, this.kaSearch, this.kaTableCount, this.kaTableOffset, this.kaDesc);
  }

  formatDistance(distance: number): string {
    if (distance > 1000) {
      return (distance / 1000).toFixed(2) + 'km';
    } else {
      return distance + 'm';
    }
  }

  selectAll(selected: boolean): void {
    this.allSelected = selected;
    if (this.keepAliveData) {
      this.keepAliveData.forEach((elem) => (elem.selected = selected));
    }
  }

  selectRow(selected: any): void {
    const row = this.keepAliveData.find((elem) => elem.id == selected.id);
    if (row) {
      row.selected = selected.selected;
      this.allSelected = false;
    }
  }

  ngOnInit() {
    this.getKeepAliveDevicesRequestBody.GroupID = this.auth.GetGroupID();
    this.authSubscription = this.auth.GetUser().subscribe((user) => {
      this.obfuscateData = user.isObfuscated;
      if (!this.firstLoad) {
        this.getKeepAliveDevices(this.kaOrderBy, '', this.kaTableCount, this.kaTableOffset, this.kaDesc);
      }
    });

    const memory = this.memory.getDetails();
    if (memory != null && !memory.isCdr) {
      this.getKeepAliveDevices(memory.orderBy, memory.search, memory.tableCount, memory.tableOffset, memory.desc);
      this.kaSearch = memory.search;
    } else {
      this.getKeepAliveDevices(this.kaOrderBy, '', this.kaTableCount, this.kaTableOffset, this.kaDesc);
      this.kaSearch = '';
    }
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
  }

  refreshData(event) {
    setTimeout(() => {
      event.target.complete();
    }, 2000);
  }
}
